/*
 * Christmas music — mirrors assets/xs-halloween-audio.js, with one addition.
 *
 * Playback rules:
 *   1. Once per visit.  sessionStorage records that it has played, so moving
 *      between pages in the same tab never restarts it.
 *   2. Not within 10 minutes of returning.  localStorage keeps the last
 *      playback time, so a new visit or new tab inside that window stays
 *      silent.  This is what stops the jingle greeting someone three times
 *      while they compare products.
 *
 * Autoplay follows the same approach as the Halloween player: attempt to play,
 * and if the browser blocks it, start on the visitor's first interaction.
 * Playback only ever begins from a real user gesture, never by force.
 */
(() => {
  const root = document.querySelector('[data-christmas-audio]');
  if (!root || root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';

  const audio = root.querySelector('[data-christmas-audio-player]');
  const control = root.querySelector('[data-christmas-audio-control]');
  const label = root.querySelector('[data-christmas-audio-label]');

  const playedKey = 'xs-christmas-audio-played-v1';        // per visit (sessionStorage)
  const lastPlayedKey = 'xs-christmas-audio-last-v1';      // 10-minute window (localStorage)
  const mutedKey = 'xs-christmas-audio-muted';
  const cooldownMs = 10 * 60 * 1000;

  let playedThisVisit = false;
  let mutedPreference = false;
  let lastPlayed = 0;

  try {
    playedThisVisit = sessionStorage.getItem(playedKey) === 'true';
    mutedPreference = localStorage.getItem(mutedKey) === 'true';
    lastPlayed = Number(localStorage.getItem(lastPlayedKey) || 0) || 0;
  } catch (error) {
    /* Storage can be blocked in strict privacy modes; the player still works,
       it just cannot remember anything between pages. */
  }

  /* Rule 1 — already played during this visit. */
  if (playedThisVisit) {
    root.hidden = true;
    return;
  }

  /* Rule 2 — played recently, in this session or a previous one. */
  if (lastPlayed && Date.now() - lastPlayed < cooldownMs) {
    root.hidden = true;
    return;
  }

  audio.src = root.dataset.audioSource;
  audio.volume = Math.min(1, Math.max(0, Number(root.dataset.audioVolume || 35) / 100));
  audio.muted = mutedPreference;

  const setStorage = (storage, key, value) => {
    try {
      storage.setItem(key, String(value));
    } catch (error) {
      /* Never let storage stop playback. */
    }
  };

  const updateControl = () => {
    const isPlaying = !audio.paused && !audio.ended;
    const isMuted = audio.muted;
    root.classList.toggle('is-playing', isPlaying);
    root.classList.toggle('is-muted', isMuted);
    control.setAttribute('aria-pressed', String(isMuted));

    if (!isPlaying) {
      control.setAttribute('aria-label', 'Play Christmas music');
      label.textContent = 'Play music';
    } else if (isMuted) {
      control.setAttribute('aria-label', 'Unmute Christmas music');
      label.textContent = 'Unmute music';
    } else {
      control.setAttribute('aria-label', 'Mute Christmas music');
      label.textContent = 'Mute music';
    }
  };

  const removeInteractionFallback = () => {
    document.removeEventListener('pointerdown', startAfterInteraction, true);
    document.removeEventListener('keydown', startAfterInteraction, true);
  };

  const startAudio = async () => {
    if (!audio.paused) return true;
    try {
      await audio.play();
      /* Only record the visit once playback has genuinely started, so a
         blocked autoplay attempt does not silently consume the visit. */
      setStorage(sessionStorage, playedKey, 'true');
      setStorage(localStorage, lastPlayedKey, Date.now());
      removeInteractionFallback();
      updateControl();
      return true;
    } catch (error) {
      updateControl();
      return false;
    }
  };

  function startAfterInteraction(event) {
    if (event.target instanceof Element && event.target.closest('[data-christmas-audio-control]')) return;
    startAudio();
  }

  control.addEventListener('click', async () => {
    if (audio.paused || audio.ended) {
      await startAudio();
      return;
    }

    audio.muted = !audio.muted;
    setStorage(localStorage, mutedKey, audio.muted);
    updateControl();
  });

  audio.addEventListener('play', updateControl);
  audio.addEventListener('volumechange', updateControl);
  audio.addEventListener('ended', () => {
    root.hidden = true;
  });

  document.addEventListener('pointerdown', startAfterInteraction, true);
  document.addEventListener('keydown', startAfterInteraction, true);

  updateControl();
  startAudio();
})();
